import Card from "components/card";
import React, { useState, useEffect, useRef } from "react";
import { IoPencil, IoCheckmark, IoClose } from "react-icons/io5";
import { API_BASE_URL } from "utils/api";
import axios from "axios";
import { handleComponentSave } from "./saveHelper";
import { usePermission } from "hooks/usePermission";

const Details = ({ lead, updateLead, showToast, setCurrentLead }) => {
  const { can } = usePermission();
  const canUpdateLead = can("lead_management", "update");
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [careOfSearchTerm, setCareOfSearchTerm] = useState("");
  const [selectedCareOfName, setSelectedCareOfName] = useState("");
  const [careOfList, setCareOfList] = useState([]);
  const [careOfPage, setCareOfPage] = useState(1);
  const [isCareOfLoading, setIsCareOfLoading] = useState(false);
  const [showCareOfDropdown, setShowCareOfDropdown] = useState(false);
  const careOfDropdownRef = useRef(null);

  const fields = [
    { label: "Estimated Amount", key: "estimatedValue", type: "number" },
    { label: "Estimated Hours", key: "estimatedHours", type: "number" },
    { label: "Actual Hours", key: "actualHours", type: "number" },
    { label: "Due Date", key: "dueDate", type: "date" },
    { label: "Start Date", key: "startDate", type: "date" },
    { label: "Completed Date", key: "completedDate", type: "date" },
    { label: "Care Of", key: "careOf", type: "search-dropdown" },
    { label: "Next Follow Up", key: "nextFollowUpAt", type: "datetime-local" },
    { label: "Last Contacted", key: "lastContactedAt", type: "datetime-local" },
  ];

  // Initialize data on mount
  useEffect(() => {
    if (lead) {
      const initialData = {
        estimatedValue: lead.estimatedValue ?? "",
        estimatedHours: lead.estimatedHours ?? "",
        actualHours: lead.actualHours ?? "",
        dueDate: lead.dueDate ?? "",
        startDate: lead.startDate ?? "",
        completedDate: lead.completedDate ?? "",
        careOf: lead.careOf ?? "",
        nextFollowUpAt: lead.nextFollowUpAt ?? "",
        lastContactedAt: lead.lastContactedAt ?? "",
      };
      setEditedData(initialData);
      setOriginalData(initialData);

      if (lead?.careOf) {
        if (typeof lead.careOf === "object" && lead.careOf.name) {
          setSelectedCareOfName(lead.careOf.name);
          setCareOfSearchTerm(lead.careOf.name);
        } else if (typeof lead.careOf === "string") {
          setSelectedCareOfName(lead.careOf);
          setCareOfSearchTerm(lead.careOf);
        }
      }
    }
  }, [lead]);

  const toLocalInputValue = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return localDate.toISOString();
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    return toLocalInputValue(dateString).slice(0, 16);
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrencyForDisplay = (value) => {
    if (!value && value !== 0) return "-";
    return new Intl.NumberFormat("en-IN", {
      style: "decimal",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (careOfDropdownRef.current && !careOfDropdownRef.current.contains(event.target)) {
        setShowCareOfDropdown(false);
      }
    };

    if (showCareOfDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showCareOfDropdown]);

  const searchCareOf = async (searchTerm = "", page = 1) => {
    try {
      setIsCareOfLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/care-of/search`, {
        params: {
          q: searchTerm,
          page,
          limit: 10,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data?.data) {
        const data = response.data.data;
        const matched = data.matched?.data || [];
        const remaining = data.remaining?.data || [];

        if (page === 1) {
          setCareOfList([...matched, ...remaining]);
        } else {
          setCareOfList((prev) => [...prev, ...remaining]);
        }
        setCareOfPage(page);
      }
    } catch (error) {
      console.error("Error searching care of:", error);
    } finally {
      setIsCareOfLoading(false);
    }
  };

  const handleCareOfChange = (careOfId, careOfName = "") => {
    setEditedData((prev) => ({ ...prev, careOf: careOfId }));
    setSelectedCareOfName(careOfName);
    setCareOfSearchTerm(careOfName);
    setShowCareOfDropdown(false);
    setFieldErrors((prev) => {
      const copy = { ...prev };
      delete copy.careOf;
      return copy;
    });
  };

  const handleCareOfSearch = (value) => {
    setCareOfSearchTerm(value);
    setCareOfPage(1);
    if (value.trim() || !value) {
      searchCareOf(value);
    }
  };

  const numericFields = new Set([
    "estimatedValue",
    "receivedAmount",
    "estimatedHours",
    "actualHours",
  ]);

  const sanitizeNumericValue = (value) => {
    if (value === "") return "";
    let sanitized = value.replace(/[^0-9.]/g, "");
    const parts = sanitized.split(".");
    if (parts.length > 1) {
      sanitized = `${parts[0]}.${parts.slice(1).join("")}`;
    }
    if (sanitized.startsWith(".")) {
      sanitized = `0${sanitized}`;
    }
    return sanitized;
  };

  const handleFieldChange = (key, value) => {
    const finalValue = numericFields.has(key) ? sanitizeNumericValue(value) : value;
    setEditedData((prev) => ({ ...prev, [key]: finalValue }));
    setFieldErrors((prev) => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
  };

  const handleNumericKeyDown = (e) => {
    const allowedKeys = [
      "Backspace",
      "Tab",
      "Enter",
      "Escape",
      "ArrowLeft",
      "ArrowRight",
      "Delete",
      "Home",
      "End",
    ];

    if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) return;
    if (/^[0-9]$/.test(e.key)) return;
    if (e.key === "." && !e.currentTarget.value.includes(".")) return;

    e.preventDefault();
  };

  const handleNumericPaste = (e) => {
    const pasteValue = e.clipboardData.getData("text");
    const sanitizedValue = sanitizeNumericValue(pasteValue);
    if (sanitizedValue !== pasteValue) {
      e.preventDefault();
      const { selectionStart = 0, selectionEnd = 0, value } = e.target;
      const newValue = `${value.slice(0, selectionStart)}${sanitizedValue}${value.slice(selectionEnd)}`;
      const key = e.target.name;
      if (key) {
        handleFieldChange(key, sanitizeNumericValue(newValue));
      }
    }
  };

  const handleCareOfDropdownScroll = (e) => {
    const element = e.target;
    if (
      element.scrollHeight - element.scrollTop <= element.clientHeight + 50 &&
      !isCareOfLoading
    ) {
      searchCareOf(careOfSearchTerm, careOfPage + 1);
    }
  };

  const parseLocalDateValue = (value) => {
    if (!value) return null;
    if (value.includes("T")) {
      return new Date(value);
    }
    const [year, month, day] = value.split("-");
    return new Date(Number(year), Number(month) - 1, Number(day));
  };

  const validateDates = () => {
    setFieldErrors({});
    const { dueDate, startDate, completedDate, nextFollowUpAt, lastContactedAt } = editedData;

    const start = parseLocalDateValue(startDate);
    const due = parseLocalDateValue(dueDate);
    const completed = parseLocalDateValue(completedDate);
    const nextFollowUp = parseLocalDateValue(nextFollowUpAt);
    const lastContacted = parseLocalDateValue(lastContactedAt);

    const errors = {};

    if (dueDate && startDate) {
      if (!due || due <= start) {
        errors.dueDate = "Due Date must be greater than Start Date.";
      }
    }

    if (completedDate && startDate) {
      if (!completed || completed <= start) {
        errors.completedDate = "Completed Date must be greater than Start Date.";
      }
    }

    if (lastContactedAt && nextFollowUpAt) {
      if (!lastContacted || !nextFollowUp || lastContacted > nextFollowUp) {
        errors.lastContactedAt = "Last Contacted must be before or equal to Next Follow Up.";
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateDates()) return;
    setIsSaving(true);
    try {
      const result = await handleComponentSave(
        lead,
        editedData,
        updateLead,
        showToast,
        setCurrentLead
      );

      if (result && result.success) {
        setIsEditing(false);
        setOriginalData(editedData);
        setFieldErrors({});
      } else if (result && result.errors) {
        setFieldErrors(result.errors);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedData(originalData);
    setIsEditing(false);
  };

  return (
    <Card extra={"w-full h-full p-3"}>
      {/* Header */}
      <div className="mb-8 mt-2 w-full flex items-center justify-between">
        <h4 className="px-2 text-xl font-bold text-navy-700 dark:text-white">
          Additional Details
        </h4>
        {!isEditing ? (
          canUpdateLead && (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2 rounded-lg bg-brand-500 px-3 py-1.5 text-white hover:bg-brand-600 transition-colors"
              title="Edit details"
            >
              <IoPencil className="text-xl" />
            </button>
          )
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={handleCancel}
              disabled={isSaving}
              className="flex items-center gap-2 rounded-lg bg-gray-500 px-3 py-1.5 text-white hover:bg-gray-600 disabled:opacity-60 transition-colors"
            >
              <IoClose className="text-lg" />
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-2 rounded-lg bg-green-500 px-3 py-1.5 text-white hover:bg-green-600 disabled:opacity-60 transition-colors"
            >
              <IoCheckmark className="text-lg" />
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        )}
      </div>
      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-2">
        {fields.map((field) => (
          <div
            key={field.key}
            className="flex flex-col items-start justify-center rounded-2xl bg-white bg-clip-border px-3 py-4 shadow-3xl shadow-shadow-500 dark:!bg-navy-700 dark:shadow-none"
          >
            <div className="flex items-center justify-between w-full mb-2">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {field.label}
              </p>
            </div>
            {isEditing ? (
              field.type === "search-dropdown" ? (
                <div className="relative w-full" ref={careOfDropdownRef}>
                  <input
                    type="text"
                    value={careOfSearchTerm}
                    onChange={(e) => handleCareOfSearch(e.target.value)}
                    onFocus={() => setShowCareOfDropdown(true)}
                    placeholder="Search Care Of..."
                    className="bg-transparent border-transparent w-full border-b text-base font-medium text-navy-700 hover:border-gray-300 focus:border-brand-500 focus:outline-none dark:text-white"
                  />
                  {showCareOfDropdown && (
                    <div
                      className="absolute top-full left-0 right-0 z-50 mt-2 max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-navy-600 dark:bg-navy-800"
                      onScroll={handleCareOfDropdownScroll}
                    >
                      {isCareOfLoading && careOfList.length === 0 ? (
                        <div className="px-3 py-2 text-center text-sm text-gray-500 dark:text-gray-400">
                          Loading...
                        </div>
                      ) : careOfList.length === 0 ? (
                        <div className="px-3 py-2 text-center text-sm text-gray-500 dark:text-gray-400">
                          No results found
                        </div>
                      ) : (
                        careOfList.map((careOf) => (
                          <div
                            key={careOf._id}
                            onClick={() => handleCareOfChange(careOf._id, careOf.name)}
                            className="cursor-pointer border-b border-gray-100 px-3 py-2 hover:bg-blue-50 dark:border-navy-600 dark:hover:bg-blue-900/20"
                          >
                            <p className="text-sm font-medium text-navy-700 dark:text-white">
                              {careOf.name}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {careOf.email} • {careOf.phone}
                            </p>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              ) : field.type === "datetime-local" ? (
                <input
                  type={field.type}
                  value={formatDateForInput(editedData[field.key])}
                  onChange={(e) => handleFieldChange(field.key, e.target.value)}
                  className="bg-transparent border-transparent w-full border-b text-base font-medium text-navy-700 hover:border-gray-300 focus:border-brand-500 focus:outline-none dark:text-white"
                  placeholder={`Enter ${field.label.toLowerCase()}`}
                />
              ) : field.type === "date" ? (
                <input
                  type="date"
                  value={editedData[field.key] ? toLocalInputValue(editedData[field.key]).slice(0, 10) : ""}
                  onChange={(e) => handleFieldChange(field.key, e.target.value)}
                  className="bg-transparent border-transparent w-full border-b text-base font-medium text-navy-700 hover:border-gray-300 focus:border-brand-500 focus:outline-none dark:text-white"
                  placeholder={`Enter ${field.label.toLowerCase()}`}
                />
              ) : (
                <input
                  type={numericFields.has(field.key) ? "text" : field.type}
                  name={field.key}
                  value={editedData[field.key] || ""}
                  onChange={(e) => handleFieldChange(field.key, e.target.value)}
                  onKeyDown={numericFields.has(field.key) ? handleNumericKeyDown : undefined}
                  onPaste={numericFields.has(field.key) ? handleNumericPaste : undefined}
                  inputMode={numericFields.has(field.key) ? "decimal" : undefined}
                  className="bg-transparent border-transparent w-full border-b text-base font-medium text-navy-700 hover:border-gray-300 focus:border-brand-500 focus:outline-none dark:text-white"
                  placeholder={`Enter ${field.label.toLowerCase()}`}
                />
              )
            ) : (
              <p className="text-base font-medium text-navy-700 dark:text-white">
                {field.type === "datetime-local"
                  ? formatDateForDisplay(lead?.[field.key])
                  : field.type === "date"
                    ? (lead?.[field.key] ? new Date(lead?.[field.key]).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" }) : "-")
                    : field.key === "careOf"
                      ? selectedCareOfName || "-"
                      : ["estimatedValue"].includes(field.key)
                        ? formatCurrencyForDisplay(lead?.[field.key])
                        : lead?.[field.key] || "-"}
              </p>
            )}
                {isEditing && fieldErrors[field.key] && (
                  <p className="mt-1 text-xs text-red-600">{fieldErrors[field.key]}</p>
                )}
          </div>
        ))}
      </div>
    </Card>
  );
};

export default Details;
